import React from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Button, TextInput } from "@/components";
import { Globe, Loader2 } from "lucide-react";
import type { ScrapeBrandParams } from "../interfaces";

const scrapeSchema = yup.object({
  url: yup.string().url("Must be a valid URL").required("URL is required"),
});

type ScrapeFormData = yup.InferType<typeof scrapeSchema>;

interface ScrapeBrandFormProps {
  onSubmit: (data: ScrapeBrandParams) => Promise<void>;
  onCancel?: () => void;
  isLoading?: boolean;
}

export const ScrapeBrandForm: React.FC<ScrapeBrandFormProps> = ({
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ScrapeFormData>({
    resolver: yupResolver(scrapeSchema) as any,
    defaultValues: {
      url: "",
    },
  });

  const onFormSubmit = async (data: ScrapeFormData) => {
    await onSubmit({
      url: data.url,
    });
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
          <Globe className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h3 className="font-semibold">Scrape Brand from Website</h3>
          <p className="text-sm text-muted-foreground">
            Automatically extract brand information from a website URL
          </p>
        </div>
      </div>

      <TextInput
        label="Website URL"
        placeholder="https://www.example.com"
        error={!!errors.url}
        helperText={
          errors.url?.message ||
          "We'll extract title, description, images, videos, and brand info"
        }
        autoFocus
        {...register("url")}
      />

      <div className="bg-muted/50 rounded-lg p-4 space-y-2">
        <p className="text-sm font-medium">What gets scraped:</p>
        <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
          <li>Title and description</li>
          <li>Important images</li>
          <li>Videos</li>
          <li>Brand information from meta tags and content</li>
        </ul>
      </div>

      <div className="flex gap-4 justify-end">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Scraping...
            </>
          ) : (
            <>
              <Globe className="h-4 w-4 mr-2" />
              Scrape Brand
            </>
          )}
        </Button>
      </div>
    </form>
  );
};
